import { useSyncExternalStore } from 'react'
import type { Divisie, Ik, LivePad, Soort } from '../../gedeeld/types'

/* ===========================================================================
 *  Wat de hele app deelt
 *
 *  Dezelfde kleine winkel als in IT en Projects: een object, een lijst
 *  luisteraars, en useSyncExternalStore. Wat erin staat is wat bijna elk
 *  scherm nodig heeft: wie je bent, welke administraties je mag zien, de
 *  catalogus van het archief, en welke bv's je nu bekijkt.
 *
 *  De keuze van bv's is één keuze voor de hele app, en blijft bewaard in
 *  deze browser. Wie de hele dag naar Venlo kijkt, hoeft dat niet op elk
 *  scherm opnieuw te zeggen.
 * =========================================================================== */

interface Staat {
  ik: Ik | null
  management: boolean
  divisies: Divisie[]
  catalogus: Soort[]
  /** Per soort de velden die Exact echt accepteerde. */
  velden: Record<string, string[]>
  paden: LivePad[]
  mongo: boolean
  /** Mag relaties aanmaken in Exact (0177). */
  magRelaties: boolean
  /** Welke bv's je bekijkt. Leeg = alle die je mag zien. */
  keuze: string[]
  meldingen: { id: number; soort: 'ok' | 'fout' | 'info'; tekst: string }[]
  zoekOpen: boolean
}

const KEUZE = 'tw.exact.bvs'

function leesKeuze(): string[] {
  try {
    const w = JSON.parse(localStorage.getItem(KEUZE) ?? '[]')
    return Array.isArray(w) ? w.map(String) : []
  } catch {
    return []
  }
}

let staat: Staat = {
  ik: null,
  management: false,
  divisies: [],
  catalogus: [],
  velden: {},
  paden: [],
  mongo: true,
  magRelaties: false,
  keuze: leesKeuze(),
  meldingen: [],
  zoekOpen: false,
}

const luisteraars = new Set<() => void>()

export function zet(deel: Partial<Staat>) {
  staat = { ...staat, ...deel }
  for (const l of luisteraars) l()
}

export function lees(): Staat { return staat }

function abonneer(l: () => void) {
  luisteraars.add(l)
  return () => { luisteraars.delete(l) }
}

export function useStaat<T>(kies: (s: Staat) => T): T {
  return useSyncExternalStore(abonneer, () => kies(staat))
}

/* ------------------------------------------------------------------ *
 *  De bv's
 * ------------------------------------------------------------------ */

export function kiesBvs(codes: string[]) {
  const mag = new Set(staat.divisies.map((d) => d.code))
  const schoon = codes.filter((c) => mag.has(c))
  /* Alles gekozen is hetzelfde als niets gekozen; bewaar het als niets, dan
     komt een nieuwe bv er vanzelf bij. */
  const opslaan = schoon.length === staat.divisies.length ? [] : schoon
  try { localStorage.setItem(KEUZE, JSON.stringify(opslaan)) } catch { /* privévenster */ }
  zet({ keuze: opslaan })
}

/** De bv's die je nu bekijkt, als codes. Nooit leeg zolang je er één mag zien. */
export function gekozenBvs(s: Staat = staat): string[] {
  const mag = new Set(s.divisies.map((d) => d.code))
  const k = s.keuze.filter((c) => mag.has(c))
  return k.length ? k : s.divisies.map((d) => d.code)
}

export const useGekozenBvs = () => {
  const keuze = useStaat((s) => s.keuze)
  const divisies = useStaat((s) => s.divisies)
  const mag = new Set(divisies.map((d) => d.code))
  const k = keuze.filter((c) => mag.has(c))
  return k.length ? k : divisies.map((d) => d.code)
}

export function bvNaam(code: string | null | undefined): string {
  if (!code) return ''
  return staat.divisies.find((d) => d.code === code)?.naam ?? code
}

/** "Truckwash 1 Venlo B.V." → "Venlo". Voor kolomkoppen waar de ruimte op is. */
export function bvKort(code: string | null | undefined): string {
  const n = bvNaam(code)
  return n.replace(/^Truckwash ?1 /i, '').replace(/ B\.?V\.?$/i, '').trim() || n
}

export function soortVan(sleutel: string | null | undefined): Soort | null {
  return staat.catalogus.find((s) => s.sleutel === sleutel) ?? null
}

/** De velden van een soort: wat Exact accepteerde, anders de kandidaten uit de catalogus. */
export function veldenVan(sleutel: string): string[] {
  const echt = staat.velden[sleutel]
  if (echt && echt.length) return echt
  return soortVan(sleutel)?.velden ?? []
}

/* ------------------------------------------------------------------ *
 *  Meldingen in beeld
 * ------------------------------------------------------------------ */

let volgnummer = 0

export function meld(tekst: string, soort: 'ok' | 'fout' | 'info' = 'ok') {
  const id = ++volgnummer
  zet({ meldingen: [...staat.meldingen, { id, soort, tekst }].slice(-4) })
  setTimeout(() => zet({ meldingen: staat.meldingen.filter((m) => m.id !== id) }), soort === 'fout' ? 8000 : 3200)
}

export const meldFout = (e: unknown) => {
  if (e instanceof DOMException && e.name === 'AbortError') return
  meld(e instanceof Error ? e.message : String(e), 'fout')
}
